/**
 * <cba-reviews> — the Judge.me reviews list for the reviews drawer and the
 * reviews page (plan 4.2). Markup: snippets/cba-reviews-list.liquid.
 *
 *   <cba-reviews data-shop data-token data-write-url [data-autoload]>
 *     <script type="application/json" data-reviews-text>{…strings…}</script>
 *     …summary, filters, sort, cards, "Show more"…
 *   </cba-reviews>
 *
 * - load(productId): fetch one product's reviews, or the whole shop's with no
 *   id. data-autoload loads the shop at once (the page). The drawer calls
 *   load() on every open.
 * - Reviews come from Judge.me's public widget API, page by page; its HTML is
 *   parsed inert (DOMParser runs no handlers and loads no images,
 *   THEME-STANDARD §8.2) and rebuilt with textContent, never innerHTML.
 * - Filter and sort run on the server: each star filter or sort is a "plan" of
 *   one or more queries paged in order (the API has no lowest-first sort, so
 *   that walks 1★ to 5★). A generation counter drops answers from an older
 *   query.
 *
 * Any [data-open-reviews-drawer] element opens the reviews drawer
 * (<cba-dialog name="reviews">) and loads it, for its data-product-id or the
 * whole shop. One that is not a link or button also opens on Enter and Space.
 */
(() => {
  const API = 'https://judge.me/api/v1/widgets/';
  const PER_PAGE = 25; // Judge.me's all_reviews_page caps a page at 25
  const FIRST = 20;    // reviews shown at first
  const MORE = 10;     // reviews added per "Show more"
  const DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); // 06 "Oct 6, 2026"
  const NUM = new Intl.NumberFormat('en-US');
  const PLURAL = new Intl.PluralRules('en-US');

  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  };
  const fill = (template, values) => template.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (k in values ? values[k] : m));

  // One star, on or off: the snippet's drawing (<template data-reviews-icon-star>).
  let starTemplate = null;
  function star(on) {
    const wrap = el('span', 'cba-reviews__star' + (on ? '' : ' cba-reviews__star--off'));
    wrap.dataset.star = on ? 'full' : 'empty';
    if (starTemplate) wrap.appendChild(starTemplate.content.cloneNode(true));
    return wrap;
  }
  function stars(rating, label) {
    const row = el('span', 'cba-flex');
    const rounded = Math.round(rating);
    for (let i = 1; i <= 5; i++) row.appendChild(star(i <= rounded));
    if (label) { row.setAttribute('role', 'img'); row.setAttribute('aria-label', label); }
    else row.setAttribute('aria-hidden', 'true');
    return row;
  }

  // Judge.me review HTML -> plain review objects.
  function parseReviews(html) {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    return [...doc.querySelectorAll('.jdgm-rev')].map((rev) => {
      const q = (s) => rev.querySelector(s);
      const text = (s) => { const n = q(s); return n ? n.textContent.trim() : ''; };
      const score = q('[data-score]');
      const stamp = q('.jdgm-rev__timestamp');
      const raw = stamp ? (stamp.getAttribute('data-content') || stamp.textContent.trim()) : '';
      let date = '';
      if (raw) {
        const d = new Date(raw.replace(' UTC', 'Z').replace(' ', 'T'));
        date = isNaN(d) ? raw : DATE.format(d);
      }
      const link = q('.jdgm-rev__prod-link');
      const pic = q('.jdgm-rev__product-picture img, .jdgm-rev__pic-img');
      return {
        id: rev.getAttribute('data-review-id') || '',
        rating: score ? parseFloat(score.getAttribute('data-score')) || 0 : 0,
        title: text('.jdgm-rev__title'),
        body: text('.jdgm-rev__body'),
        author: text('.jdgm-rev__author'),
        date,
        iso: raw,
        verified: rev.getAttribute('data-verified-buyer') === 'true' || !!q('.jdgm-rev__buyer-badge'),
        product: link ? { name: link.textContent.trim(), url: link.getAttribute('href') || '' } : null,
        image: pic ? (pic.getAttribute('data-src') || pic.getAttribute('src') || '') : '',
      };
    });
  }

  // The shop-wide star breakdown that Judge.me embeds in every answer.
  function parseHistogram(html) {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    const rows = doc.querySelectorAll('[data-rating][data-frequency]');
    if (!rows.length) return null;
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    rows.forEach((row) => {
      const r = parseInt(row.getAttribute('data-rating'), 10);
      if (r >= 1 && r <= 5) counts[r] = parseInt(row.getAttribute('data-frequency'), 10) || 0;
    });
    return counts;
  }

  // A Shopify CDN image at a small width (the thumbnail beside the product name).
  function thumb(url) {
    if (!/cdn\.shopify\.com/.test(url)) return url;
    return url + (url.includes('?') ? '&' : '?') + 'width=96';
  }

  class CbaReviews extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try { this.text = JSON.parse(this.querySelector('[data-reviews-text]').textContent); } catch (e) { this.text = {}; }
      const checkTpl = this.querySelector('[data-reviews-icon-check]');
      this.checkIcon = checkTpl ? checkTpl.content : null;
      starTemplate = starTemplate || this.querySelector('[data-reviews-icon-star]');
      this.gen = 0;
      this.$ = (name) => this.querySelector(`[data-reviews-${name}]`);

      this.$('sort').addEventListener('change', (e) => { this.sort = e.target.value; this.query(); });
      this.$('filters').addEventListener('click', (e) => {
        const pill = e.target.closest('[data-filter]');
        if (!pill || pill.dataset.filter === this.filter) return;
        this.setFilter(pill.dataset.filter);
        this.query();
      });
      this.$('more-btn').addEventListener('click', () => this.showMore());
      if (this.hasAttribute('data-autoload')) this.load(null);
    }

    t(key, values) { return fill(this.text[key] || '', values || {}); }
    tCount(base, count) {
      const form = PLURAL.select(count) === 'one' ? 'One' : 'Other';
      return this.t(base + form, { count: NUM.format(count) });
    }

    url(bucket, page) {
      const shop = encodeURIComponent(this.dataset.shop);
      const token = encodeURIComponent(this.dataset.token);
      let u = this.productId
        ? `${API}product_review?shop_domain=${shop}&api_token=${token}&external_id=${encodeURIComponent(this.productId)}&per_page=${PER_PAGE}&page=${page}`
        : `${API}all_reviews_page?shop_domain=${shop}&api_token=${token}&per_page=${PER_PAGE}&page=${page}`;
      if (bucket.rating) u += `&filter_rating=${bucket.rating}`;
      if (bucket.sort) u += `&sort_by=${bucket.sort}`;
      return u;
    }

    // The queries a filter and sort need, in the order they are read.
    plan() {
      if (this.filter !== 'all') return [{ rating: parseInt(this.filter, 10) }];
      if (this.sort === 'newest') return [{ sort: 'most_recent' }];
      if (this.sort === 'lowest') return [1, 2, 3, 4, 5].map((r) => ({ rating: r }));
      // all_reviews_page is already highest-first; product_review has no set order.
      return this.productId ? [5, 4, 3, 2, 1].map((r) => ({ rating: r })) : [{}];
    }

    resetCursor() {
      this.reviews = [];
      this.seen = new Set();
      this.buckets = this.plan();
      this.bucket = 0;
      this.page = 0;
      this.done = false;
      this.limit = FIRST;
    }

    async load(productId) {
      this.gen += 1;
      const gen = this.gen;
      this.productId = productId || null;
      this.sort = 'highest';
      this.counts = null;
      this.average = null;
      this.$('sort').value = 'highest';
      this.setFilter('all');
      this.$('more').hidden = true;
      this.$('cards').replaceChildren();
      this.dataset.state = 'loading';
      this.setStatus(this.t('loading'));
      this.resetCursor();

      this.fetchAverage(gen);
      try {
        await this.fill(gen, FIRST);
      } catch (e) {
        if (gen === this.gen) { this.dataset.state = 'error'; this.setStatus(this.t('error')); }
        return;
      }
      if (gen !== this.gen) return;
      this.renderSummary();
      if (!this.reviews.length) { this.dataset.state = 'empty'; this.setStatus(this.t('empty')); this.$('count').textContent = this.tCount('count', 0); return; }
      this.dataset.state = 'ready';
      this.setStatus('');
      this.renderCards();
    }

    setFilter(value) {
      this.filter = value;
      this.$('filters').querySelectorAll('[data-filter]').forEach((p) => p.setAttribute('aria-pressed', String(p.dataset.filter === value)));
    }

    setStatus(message) { this.$('status').textContent = message; }

    // The headline average: the product's badge, or the shop's rating.
    fetchAverage(gen) {
      const shop = encodeURIComponent(this.dataset.shop);
      const token = encodeURIComponent(this.dataset.token);
      const u = this.productId
        ? `${API}preview_badge?shop_domain=${shop}&external_id=${encodeURIComponent(this.productId)}&api_token=${token}`
        : `${API}all_reviews_rating?shop_domain=${shop}&api_token=${token}`;
      fetch(u).then((r) => r.json()).then((d) => {
        if (gen !== this.gen) return;
        if (this.productId) {
          const m = (d.badge || '').match(/data-average-rating='([\d.]+)'/);
          if (m) this.average = parseFloat(m[1]);
        } else if (d && d.all_reviews_rating != null) {
          this.average = parseFloat(d.all_reviews_rating);
        }
        if (this.dataset.state === 'ready' || this.dataset.state === 'empty') this.renderSummary();
      }).catch(() => {});
    }

    async nextPage(gen) {
      if (gen !== this.gen || this.done) return;
      this.page += 1;
      const r = await fetch(this.url(this.buckets[this.bucket], this.page));
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();
      if (gen !== this.gen) return;
      if (!this.counts) this.counts = parseHistogram(data.all_reviews_header || data.widget || '');
      const batch = parseReviews(data.all_reviews || data.widget || '');
      if (!batch.length) {
        this.bucket += 1;
        this.page = 0;
        if (this.bucket >= this.buckets.length) { this.done = true; return; }
        await this.nextPage(gen);
        return;
      }
      batch.forEach((rev) => {
        const key = rev.id || `${rev.author}|${rev.body.slice(0, 40)}`;
        if (this.seen.has(key)) return;
        this.seen.add(key);
        this.reviews.push(rev);
      });
    }

    async fill(gen, target) {
      while (gen === this.gen && !this.done && this.reviews.length < target) await this.nextPage(gen);
    }

    total() {
      if (this.counts) {
        if (this.filter !== 'all') return this.counts[parseInt(this.filter, 10)] || 0;
        return Object.values(this.counts).reduce((a, b) => a + b, 0);
      }
      return this.reviews.length;
    }

    renderSummary() {
      const counts = this.counts || this.reviews.reduce((c, r) => { const s = Math.round(r.rating); if (c[s] != null) c[s] += 1; return c; }, { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
      const all = Object.values(counts).reduce((a, b) => a + b, 0);
      const avg = this.average != null ? this.average : (all ? Object.entries(counts).reduce((s, [k, v]) => s + k * v, 0) / all : 0);
      const avgText = all ? avg.toFixed(2).replace(/0$/, '') : '–';

      const avgStars = this.$('avg-stars');
      avgStars.replaceChildren(...[1, 2, 3, 4, 5].map((i) => star(i <= Math.round(avg))));
      avgStars.setAttribute('role', 'img');
      avgStars.setAttribute('aria-label', this.t('ratingLabel', { rating: avgText }));
      this.$('avg-text').textContent = this.t('average', { rating: avgText });
      this.$('based-on').textContent = this.tCount('basedOn', all);

      this.querySelectorAll('[data-bar]').forEach((row) => {
        const n = counts[row.dataset.bar] || 0;
        row.querySelector('[data-bar-fill]').style.width = all ? `${(n / all) * 100}%` : '0%';
        row.querySelector('[data-bar-count]').textContent = NUM.format(n);
      });
    }

    // A new filter or sort: start the list over from the server.
    async query() {
      this.gen += 1;
      const gen = this.gen;
      this.resetCursor();
      this.dataset.state = 'loading'; // keeps the list's height while it refills
      this.$('cards').replaceChildren();
      this.$('more').hidden = true;
      this.$('count').textContent = this.t('loadingMore');
      try { await this.fill(gen, FIRST); } catch (e) { if (gen === this.gen) { this.dataset.state = 'error'; this.$('count').textContent = this.t('error'); } return; }
      if (gen !== this.gen) return;
      this.dataset.state = 'ready';
      this.renderCards();
    }

    async showMore() {
      const btn = this.$('more-btn');
      if (btn.disabled) return;
      const gen = this.gen;
      const target = this.limit + MORE;
      if (this.reviews.length < target && !this.done) {
        btn.disabled = true;
        btn.textContent = this.t('loadingMore');
        try { await this.fill(gen, target); } catch (e) { /* keep what loaded */ }
        btn.disabled = false;
        btn.textContent = this.t('showMore');
        if (gen !== this.gen) return;
      }
      const first = this.limit;
      this.limit = target;
      this.renderCards();
      // Keyboard and screen reader users continue at the first new review.
      const next = this.$('cards').children[first];
      if (next && document.activeElement === btn) { next.setAttribute('tabindex', '-1'); next.focus({ preventScroll: false }); }
    }

    card(rev) {
      const li = el('li', 'cba-reviews__card');
      const top = el('div', 'cba-flex-between cba-gap-inline');
      top.appendChild(stars(rev.rating, this.t('ratingLabel', { rating: Math.round(rev.rating) })));
      const date = el('time', 'cba-type-meta cba-reviews__date', rev.date);
      if (rev.iso) date.dateTime = rev.iso.replace(' UTC', 'Z').replace(' ', 'T');
      top.appendChild(date);
      li.appendChild(top);

      if (!this.productId && rev.product && rev.product.name) {
        const a = el('a', 'cba-type-body-small cba-reviews__product');
        a.href = rev.product.url;
        if (rev.image) {
          const img = el('img', 'cba-reviews__product-img');
          img.src = thumb(rev.image);
          img.alt = '';
          img.loading = 'lazy';
          img.width = 44;
          img.height = 44;
          a.appendChild(img);
        }
        a.appendChild(el('span', '', this.t('about', { product: rev.product.name })));
        li.appendChild(a);
      }
      if (rev.title) li.appendChild(el('p', 'cba-type-card-title cba-reviews__card-title', rev.title));
      if (rev.body) li.appendChild(el('p', 'cba-type-body cba-reviews__text', rev.body));

      const by = el('div', 'cba-type-body-small cba-reviews__author');
      by.appendChild(el('p', '', rev.author || this.t('anonymous')));
      if (rev.verified) {
        const v = el('span', 'cba-flex cba-items-center cba-gap-label');
        if (this.checkIcon) v.appendChild(this.checkIcon.cloneNode(true));
        v.appendChild(el('span', '', this.t('verified')));
        by.appendChild(v);
      }
      li.appendChild(by);
      return li;
    }

    renderCards() {
      const shown = this.reviews.slice(0, this.limit);
      this.$('cards').replaceChildren(...shown.map((r) => this.card(r)));
      const total = this.total();
      this.$('count').textContent = this.tCount('count', total);
      const more = this.limit < this.reviews.length || !this.done;
      this.$('more').hidden = !more;
      if (more) this.$('showing').textContent = this.t('showing', { shown: NUM.format(shown.length), total: NUM.format(total) });
    }
  }

  /* Once per page however often this script loads (its tag comes with every
     reviews list): the element, and with it the triggers below. */
  if (customElements.get('cba-reviews')) return;
  customElements.define('cba-reviews', CbaReviews);

  // Open the reviews drawer from any trigger.
  function openDrawer(trigger) {
    const host = document.querySelector('cba-dialog[name="reviews"]');
    if (!host) return;
    if (host.open) host.open(trigger);
    const list = host.querySelector('cba-reviews');
    customElements.whenDefined('cba-reviews').then(() => list && list.load(trigger.getAttribute('data-product-id')));
  }
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-open-reviews-drawer]');
    if (!trigger) return;
    e.preventDefault();
    openDrawer(trigger);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const trigger = e.target.closest('[data-open-reviews-drawer]');
    if (!trigger || /^(A|BUTTON)$/.test(trigger.tagName)) return;
    e.preventDefault();
    openDrawer(trigger);
  });
})();
